import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Eye, EyeOff, Shield } from 'lucide-react';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import { useAuthStore } from '../stores/authStore';
import { adminLogin } from '../api/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  // Redirect if already logged in (after hooks)
  if (localStorage.getItem('eriko_admin_access_token')) return <Navigate to="/" replace />;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }

    setIsPending(true);
    try {
      const data = await adminLogin(email, password);

      setAuth({ user: data.user, tokens: data.tokens });
      navigate('/', { replace: true });
    } catch (err) {
      const message = (err as Error).message;
      setError(message === 'INVALID_CREDENTIALS' ? 'Invalid email or password.' : message);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-50">
      <div className="w-full max-w-sm px-6">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-9 h-9 bg-brand-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <span className="text-lg font-semibold tracking-tight text-surface-900">Eriko</span>
            <span className="ml-1.5 text-[10px] font-bold bg-surface-800 text-white px-1.5 py-0.5 uppercase tracking-wider">
              Admin
            </span>
          </div>
        </div>

        <h2 className="text-xl font-bold text-surface-900 tracking-tight text-center">
          Reviewer Login
        </h2>
        <p className="mt-1 text-sm text-surface-500 text-center">
          Sign in to access the admin review panel.
        </p>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-surface-700">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@eriko.ai"
              className="mt-1.5"
              autoComplete="email"
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-sm font-medium text-surface-700">
              Password
            </Label>
            <div className="relative mt-1.5">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" strokeWidth={1.8} />
                ) : (
                  <Eye className="w-4 h-4" strokeWidth={1.8} />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2.5">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <Button type="submit" className="w-full h-10 text-sm font-semibold" disabled={isPending}>
            {isPending ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}
